// getMessageThumbnail.js
'use strict';
import createCachedSelector from '../resource/createCachedSelector.js';

import { PUBLIC_URL_PREFIX } from '../RemoteConfigKeys.js';
import getIsPreviewClear from '../selector/getIsPreviewClear.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getMeData from '../selector/getMeData.js';
import { getPoster, MediaPreset } from '../resource/getMediaAsset.js';
import getMessageData from '../selector/getMessageData.js';
import getAssetData from '../selector/getAssetData.js';

const isServer = typeof window === 'undefined';

/**
 * Select message thumbnail.
 * Metadata thumbnails only represent the cover (first asset), so the cover
 * uses `metadata.thumbnail_url` / `metadata.thumbnail_blurred_url`, while
 * non-cover assets use their own blurred url for the blurred preset
 * (multi-asset posts only contain image assets, whose blurred url is an image).
 * Falls back to the constructed poster.jpg url when no source is available.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} messageId - message id.
 * @param {string} assetId - asset id.
 * @param {string} [size = '128x128'] - message thumbnail size.
 * @param {string} [extension = 'jpg'] - message thumbnail extension.
 * @param {string} [preset] - message thumbnail preset. ex: 'clear', 'blurred'
 * @param {boolean} [shouldUseToken = false] - should set token on thumbnail url.
 * @return {string} Return message thumbnail url.
 */
const getMessageThumbnail = createCachedSelector(
  state => getMeData(state, 'token'),
  state => (getIsPreviewClear(state) ? 'clear' : 'blurred'),
  (state, messageId) => messageId,
  (state, messageId, assetId) => assetId,
  (state, messageId, assetId, size) => size,
  (state, messageId, assetId, size, extension) => extension,
  (state, messageId, assetId, size, extension, preset) => preset,
  (state, messageId, assetId, size, extension, preset, shouldUseToken) =>
    shouldUseToken,
  (state, messageId) => getMessageData(state, messageId, 'canViewMedia'),
  state => getRemoteConfigData(state, PUBLIC_URL_PREFIX),
  (state, messageId) => getMessageData(state, messageId, 'senderId'),
  state => getMeData(state, 'id'),
  (state, messageId) => getMessageData(state, messageId, 'thumbnailUrl'),
  (state, messageId) => getMessageData(state, messageId, 'thumbnailBlurredUrl'),
  (state, messageId) => getMessageData(state, messageId, 'assetIds'),
  (state, messageId, assetId) => getAssetData(state, [assetId], 'blurredUrl'),
  (
    token,
    trailerPreset,
    messageId,
    assetId,
    size = '128x128',
    extension = 'jpg',
    preset,
    shouldUseToken = false,
    canViewMedia = false,
    // Use `PUBLIC_URL_PREFIX` as result function parameter to generate correct poster url on SSR.
    // This solve the issue in SSR where different users with different `PUBLIC_URL_PREFIX` try to generate a poster url, but they all end up using the poster URL directly from the cache.
    // eslint-disable-next-line no-unused-vars
    publicUrlPrefix,
    senderId,
    meId,
    thumbnailUrl,
    thumbnailBlurredUrl,
    assetIds,
    assetBlurredUrl
  ) => {
    const usingPreset = preset
      ? preset
      : canViewMedia
        ? 'clear'
        : trailerPreset;

    // always show clear preset on the server,
    // and follow the remote config on the client
    const shouldUseClearPreset = isServer || usingPreset === 'clear';
    // Metadata thumbnails only represent the cover (first asset).
    const isCoverAsset =
      !assetId || !assetIds?.length || assetId === assetIds[0];

    const coverThumbnailUrl = shouldUseClearPreset
      ? thumbnailUrl
      : thumbnailBlurredUrl;
    const nonCoverThumbnailUrl = shouldUseClearPreset
      ? undefined
      : assetBlurredUrl;
    const url = getPoster({
      id: messageId,
      assetId,
      userId: senderId,
      meId,
      size,
      format: extension,
      preset: shouldUseClearPreset ? MediaPreset.SD : MediaPreset.SD_PREVIEW,
      thumbnailUrl: isCoverAsset ? coverThumbnailUrl : nonCoverThumbnailUrl,
    });
    if (shouldUseToken && token) {
      url.searchParams.set('auth', token);
    }
    return url.href;
  }
)(
  (
    state,
    messageId,
    assetId,
    size = '128x128',
    extension = 'jpg',
    preset,
    shouldUseToken = false
  ) =>
    `${messageId}:${assetId}:${size}:${extension}:${preset}:${shouldUseToken}`
);

export default getMessageThumbnail;
